import { useState } from 'react';
import type { AuthUser } from '@/features/auth/stores/authStore';
import { useDeactivateUser } from '../hooks/useUser';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { AlertCircle } from 'lucide-react';

interface DeactivateUserDialogProps {
  user: AuthUser;
}

export const DeactivateUserDialog = ({ user }: DeactivateUserDialogProps) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const deactivateMutation = useDeactivateUser();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    deactivateMutation.mutate(
      {
        id: user.id,
        payload: {
          userId: user.id,
          reason,
        },
      },
      {
        onSuccess: () => {
          setReason('');
          setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start text-orange-600 hover:text-orange-600">
          <AlertCircle className="w-4 h-4" />
          Deactivate
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Deactivate User</DialogTitle>
          <DialogDescription>
            Deactivate {user.firstName} {user.otherNames}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {deactivateMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {deactivateMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="reason">Reason (optional)</FieldLabel>
            <Input
              id="reason"
              placeholder="Why are you deactivating this user?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={deactivateMutation.isPending}
            />
          </Field>

          <div className="rounded-md bg-orange-50 p-3 text-sm text-orange-700">
            This user will no longer be able to access the system.
          </div>

          <Button
            type="submit"
            className="w-full bg-orange-600 hover:bg-orange-700"
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate User'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};